import { useState } from 'react'
import { Download } from 'lucide-react'
import { toast } from 'sonner'
import { LoadingButton } from '@/components/loading-button'
import { API_BASE_URL } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { getApiErrorMessage } from '@/lib/api-error'

type ZipParams = Record<string, string | number | string[] | undefined>

function queryString(params: ZipParams) {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    for (const item of Array.isArray(value) ? value : [value]) query.append(key, String(item))
  }
  return query.toString()
}

/** Downloads copies of the documents behind a manufacturer tab as one ZIP (USA-1758). */
export function DocumentZipButton({
  path,
  params,
  filename,
}: {
  path: string
  params: ZipParams
  filename: string
}) {
  const { getAccessToken } = useAuth()
  const [downloading, setDownloading] = useState(false)

  const download = async () => {
    setDownloading(true)
    try {
      const token = await getAccessToken()
      if (!token) throw new Error('Sign in again to download')
      const response = await fetch(`${API_BASE_URL}${path}?${queryString(params)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw await response.json().catch(() => null)
      const url = URL.createObjectURL(await response.blob())
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      anchor.click()
      URL.revokeObjectURL(url)
      toast.success(response.headers.get('X-Zip-Summary') ?? 'ZIP downloaded')
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The ZIP could not be downloaded'))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <LoadingButton
      type="button"
      variant="outline"
      className="h-9 shrink-0 gap-1.5 px-3"
      loading={downloading}
      loadingText="Preparing..."
      onClick={() => void download()}
    >
      <Download aria-hidden className="h-4 w-4" />
      Download ZIP
    </LoadingButton>
  )
}
